import { useEffect, useMemo } from "react";

import { useBuilderStore } from "@/components/builder/utils/store";
import createContextFactory from "@/utils/factory";

import useSave from "./hooks/useAutoSave";
import useBuilder from "./hooks/useBuilder";
import useCompiler from "./hooks/useCompiler";
import useFont from "./hooks/useFont";
import useListStores from "./hooks/useListStores";
import useMetadata from "./hooks/useMetadata";
import useProp from "./hooks/useProp";
import useTypesList from "./hooks/useTheme/useTypesList";
import { templateRoot } from "./hooks/useTheme/utils";
import useUI from "./hooks/useUI";

const { Provider: CMSBuilderProvider, useContext: useCMSBuilderContext } = createContextFactory(() => {
  const stores = useListStores()
  const compiler = useCompiler()
  const ui = useUI()
  const autoSave = useSave()
  const fonts = useFont()
  const meta = useMetadata()
  const blocksDefinition = useProp({})

  const definition = useDefinition()

  const types = useTypesList(definition)
  const post = useMemo(() => ({ types }), [types])

  const builder = useBuilder(compiler, post, stores)

  {
    const { editing: { setValue: setEditing }, craft: { setValue: setCraft } } = builder
    useEffect(() => {
      const [{ templates }] = types?.list ?? []

      if (templates?.length > 0) {
        setEditing(templates[0])
        setCraft(templates[0]?.definition?.craft)
      }
    }, [types, setEditing, setCraft])
  }

  return useMemo(() => ({
    builder,
    stores,
    theme: post,
    compiler,
    ui,
    autoSave,
    fonts,
    meta,
    blocksDefinition,
  }), [stores, post, compiler, ui, builder, autoSave, fonts, meta, blocksDefinition])
})

export function useEditing() {
  const { builder: { editing } } = useCMSBuilderContext()
  return editing
}

export function useCraft() {
  const { builder: { craft } } = useCMSBuilderContext()
  return craft
}

export function useStoreTemplates() {
  const { stores: { templates } } = useCMSBuilderContext()
  return templates
}

export function useStoreLayouts() {
  const { stores: { layouts } } = useCMSBuilderContext()
  return layouts
}

export function useStorePartials() {
  const { stores: { partials } } = useCMSBuilderContext()
  return partials
}

export function useThemeTypes() {
  const { theme: { types } } = useCMSBuilderContext()
  return types
}

export function usePalette() {
  const { compiler: { palette } } = useCMSBuilderContext()
  return palette
}

export function useContextBuilder() {
  const { builder } = useCMSBuilderContext()
  return builder
}

export function useContextUI() {
  const { ui } = useCMSBuilderContext()
  return ui
}

export function useFilteredProps() {
  const { ui: { filteredProps } } = useCMSBuilderContext()
  return filteredProps
}

export function useContextCompiler() {
  const { compiler } = useCMSBuilderContext()
  return compiler
}

export function useSelected() {
  const { ui: { selected } } = useCMSBuilderContext()
  return selected
}

export function useUtilities() {
  const { ui: { utilities } } = useCMSBuilderContext()

  return {
    selectedUtilities: utilities.value,
    setSelectedUtilities: utilities.setValue,
    utilitiesParsed: utilities.value.join('.'),
  }
}

export function useLeftSidebar() {
  const { ui: { leftSidebar } } = useCMSBuilderContext()

  return leftSidebar
}

export function useInteractionMode() {
  const { ui: { interactionMode } } = useCMSBuilderContext()

  return interactionMode
}

export function useResponsive() {
  const { ui: { responsive } } = useCMSBuilderContext()

  return responsive
}

export function useAvailableProps(extraProps = []) {
  const { ui: { availableProps } } = useCMSBuilderContext()
  const extraPropsBuild = extraProps.map(item => item.build())
  for (const prop of extraPropsBuild) {
    if (availableProps[prop.category]?.find(item => item.name === prop.name)) continue
    availableProps[prop.category] = [...availableProps?.[prop.category] || [], prop]
  }

  return availableProps
}

export function useAutoSave() {
  const { autoSave } = useCMSBuilderContext()
  return autoSave
}

export function usePropsIgnoreDevice() {
  const { ui: { propsIgnoreDevice } } = useCMSBuilderContext()
  return propsIgnoreDevice
}

export function useFormattingProps() {
  const { ui: { formattingProps } } = useCMSBuilderContext()
  return formattingProps
}

export function useBlocksDefinitionContext() {
  const { blocksDefinition } = useCMSBuilderContext()
  return blocksDefinition
}

export function useFontFamily() {
  const { fonts } = useCMSBuilderContext()
  return fonts
}

export {
  CMSBuilderProvider as BuilderProvider,
  useCMSBuilderContext as useBuilderContext,
  useContextBuilder as useBuilder,
  useContextUI as useUI,
  useContextCompiler as useCompiler,
}

function useDefinition() {
  const { postBuilder: { item } } = useBuilderStore()

  return useMemo(() => {
    if (!item) {
      return {
        definition: {
          types: [{
            templates: [
              templateRoot("default"),
            ],
          }],
        },
      }
    }
    const { html, craft, blocks, css, meta } = item

    return {
      definition: {
        types: [{
          templates: [{
            definition: { craft, blocks, css, meta, html },
          }],
        }],
      },
    }
  }, [item])
}
